/**
 * 구두가 바뀌었다는 소식을 **같은 브라우저의 다른 창**에 알리는 통로 (티켓 #513).
 *
 * ## 무엇을 고치는 통로인가
 * 정책 자동화 화면(`/policy/pipeline`)을 창 두 개에 나란히 띄우고, 한쪽에서 구두를 추가·수정·
 * 삭제·확정해도 다른 쪽 목록은 옛 상태로 멈춰 있었다. 이 화면은 `POLICY_HEAVY_QUERY_OPTS` 로
 * "이 창으로 돌아오면 다시 불러오게" 되어 있지만, react-query 의 그 판정은 `visibilitychange`
 * (창을 가렸다 다시 봄)에 걸린다. 두 창이 **둘 다 계속 보이는** 상태에서는 아무도 "돌아왔다"가
 * 되지 않아 영영 안 걸린다.
 *
 * ## 폴링이 아니다
 * 몇 초마다 서버에 물어보는 방식은 저녁 시간대 요청이 10배로 뛰는 이 서비스에서 특히 비싸다.
 * 그래서 **구두를 저장하는 그 순간에만** 신호 하나가 브라우저 안에서 오가고, 받은 창이 그때
 * 한 번 정책 쿼리를 무효화한다(ETag/304 라 안 바뀌었으면 본문 0). 신호 자체는 서버에 닿지 않는다.
 *
 * ## 왜 심장박동이 없나
 * 원본 창 잇기(`policyMatrixLink.ts`)·구두 조건 판 잇기(`policyVerbalPadLink.ts`)와 같은
 * `BroadcastChannel` 패턴이지만, 그 둘은 화면에 "연결됨"을 표시해야 해서 hello·alive·bye 박동이
 * 필요했다. 여기서 오가는 것은 **한 번 쏘고 끝나는 소식**뿐이라 상대가 살아 있는지 알 필요가
 * 없다 — 아무도 안 듣고 있으면 그냥 아무 일도 안 일어난다. 쓰지도 않을 박동을 2초마다 돌리는
 * 것은 순수한 낭비라 넣지 않았다.
 *
 * ## 창을 하나만 띄운 사람에게는 아무 변화가 없다
 * `BroadcastChannel` 은 **자기가 보낸 소식을 자기에게 되돌려주지 않는다**(같은 채널 객체 기준).
 * 창을 하나만 쓰면 들을 상대가 없으므로 종전과 완전히 같다. 그래도 창 하나 안에서 채널을 두 번
 * 여는 일이 생겨도 안전하도록 보낸 창의 표식(`from`)을 실어 보내고 받는 쪽이 걸러 낸다.
 * `BroadcastChannel` 이 없는 오래된 환경에서는 보내기·받기가 조용히 아무 일도 안 한다(= 종전 동작).
 */

import { useEffect, useRef, useState } from "react";

const CHANNEL = "phoneboard:policy-verbal-change";

/** 이 창의 표식 — 자기가 쏜 소식을 자기가 받아 헛되이 다시 불러오는 일을 막는다. */
const WINDOW_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

type VerbalChangeMessage = { t: "verbal-changed"; from: string };

/** 채널은 창당 하나만 연다(보내기·받기 공용). `null` = 이 환경엔 BroadcastChannel 이 없다. */
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
  return channel;
}

/**
 * "구두가 바뀌었다"를 다른 창에 알린다 — 구두 추가·수정·삭제·확정이 **서버에 반영된 뒤**에만 부른다.
 *
 * 부르는 자리는 정책 쿼리를 무효화하는 자리와 같다(`settleVerbalMutation`·구두 등록 mutation).
 * 무효화가 미뤄지는 연타 중간(다른 mutation 이 아직 도는 중)에는 신호도 함께 미뤄야 하므로,
 * 무효화를 실제로 도는 자리 안에서만 부른다.
 */
export function notifyVerbalChanged(): void {
  getChannel()?.postMessage({ t: "verbal-changed", from: WINDOW_ID } satisfies VerbalChangeMessage);
}

/**
 * 다른 창의 구두 변경 소식을 듣는다.
 *
 * @param onRemoteChange 소식이 왔을 때 할 일(대개 `invalidatePolicyQueries`) — 최신 함수가 쓰인다.
 * @returns 소식을 받은 횟수. 값이 바뀌었다는 것 자체가 "방금 남의 창에서 뭔가 바뀌었다"는 뜻이라,
 *          화면이 이걸 보고 새로 들어온 줄을 반짝이게 하는 신호로 쓴다(0 = 아직 한 번도 없음).
 */
export function useVerbalChangeListener(onRemoteChange: () => void): number {
  const [received, setReceived] = useState(0);
  const handlerRef = useRef(onRemoteChange);
  handlerRef.current = onRemoteChange;

  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;
    const onMessage = (event: MessageEvent<VerbalChangeMessage>) => {
      const msg = event.data;
      if (!msg || msg.t !== "verbal-changed" || msg.from === WINDOW_ID) return;
      setReceived((n) => n + 1);
      handlerRef.current();
    };
    ch.addEventListener("message", onMessage);
    return () => ch.removeEventListener("message", onMessage);
  }, []);

  return received;
}

// ── 바뀐 줄 반짝이기 ─────────────────────────────────────────────────────────

/** 소식을 받고 이 시간 안에 달라진 줄만 "남의 창이 바꾼 것"으로 본다(ms).
 *  소식 → 무효화 → 재조회 → 화면 반영까지의 왕복을 넉넉히 덮되, 한참 뒤의 내 편집까지
 *  반짝이지는 않을 만큼. */
const ARM_MS = 20_000;
/** 반짝이는 시간(ms) — CSS 애니메이션(1.8초)이 끝난 뒤에 클래스를 뗀다. */
export const FLASH_MS = 2_000;

const NO_FLASH: ReadonlySet<number> = new Set<number>();

/** 반짝임 판정의 재료 한 줄 — `mark` 가 달라져도 "바뀐 줄"로 본다(구두는 확정 상태를 넣는다). */
export type FlashRow = { id: number; mark?: string };

/**
 * 다른 창의 소식 직후에 **새로 들어오거나 상태가 바뀐 줄**을 잠깐 반짝이게 한다.
 *
 * 반짝임의 조건이 "소식을 받은 직후"인 게 핵심이다 — 창을 하나만 띄우고 쓰면 소식이 올 데가
 * 없으므로 무장(arm)되지 않고, 화면은 종전과 완전히 같다(티켓 #513의 명시 조건).
 *
 * @param rows 지금 화면에 그려질 줄들(순서는 상관없다)
 * @param remoteChangeSignal `useVerbalChangeListener` 가 돌려준 값 — 늘어날 때마다 무장한다
 * @param resetKey 보는 대상이 바뀌는 열쇠(예: 업체명). 바뀌면 이전 목록 기억과 무장을 함께 버린다 —
 *                 다른 업체 창을 열며 통째로 갈린 목록이 "새로 들어온 줄"로 오인되지 않게.
 */
export function useFlashOnRemoteChange(
  rows: FlashRow[],
  remoteChangeSignal: number,
  resetKey?: string | null,
): ReadonlySet<number> {
  const rowsKey = rows.map((r) => `${r.id}:${r.mark ?? ""}`).join(",");
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const prevRef = useRef<Map<number, string> | null>(null);
  const armedUntilRef = useRef(0);
  const [flashIds, setFlashIds] = useState<ReadonlySet<number>>(NO_FLASH);

  useEffect(() => {
    if (remoteChangeSignal > 0) armedUntilRef.current = Date.now() + ARM_MS;
  }, [remoteChangeSignal]);

  useEffect(() => {
    prevRef.current = null;
    armedUntilRef.current = 0;
    setFlashIds((cur) => (cur.size ? NO_FLASH : cur));
  }, [resetKey]);

  useEffect(() => {
    const next = new Map(rowsRef.current.map((r) => [r.id, r.mark ?? ""]));
    const prev = prevRef.current;
    prevRef.current = next;
    // 처음 그린 목록은 통째로 "새 줄"이라 반짝이면 안 된다 — 기준선으로만 삼는다.
    if (!prev) return;
    if (Date.now() > armedUntilRef.current) return;
    const changed = [...next].filter(([id, mark]) => !prev.has(id) || prev.get(id) !== mark);
    if (changed.length === 0) return;
    setFlashIds(new Set(changed.map(([id]) => id)));
    const timer = setTimeout(() => setFlashIds(NO_FLASH), FLASH_MS);
    return () => clearTimeout(timer);
  }, [rowsKey]);

  return flashIds;
}
